<template>
  <div>
    <CCard v-if="!hasPayrollSummaryView" class="p-4 text-center text-muted">
      You do not have permission to view Payroll Summary.
    </CCard>
    <div v-else>
    <CCard class="p-3 p-md-4 payroll-summary">
      <CRow class="align-items-center mb-3">
        <CCol lg="7" md="7" sm="12">
          <h4 class="summary-title mb-0">Payroll Summary</h4>
          <small class="summary-subtitle">Annual payroll overview and breakdown — fiscal year {{ selectedYear || "----" }}</small>
        </CCol>
       
      </CRow>

      <CCard class="year-filter-card p-3 mb-3">
        <CRow class="align-items-end">
          <CCol lg="3" md="4" sm="12">
            <label class="mb-1 field-label">Select Year</label>
            <select v-model="selectedYear" class="form-control form-control-sm" @change="loadPayrollSummary">
              <option disabled value="">- Select Year -</option>
              <option v-for="year in availableYears" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </CCol>
          <!-- <CCol lg="2" md="3" sm="12" class="mt-2 mt-md-0">
            <CButton color="primary" size="sm" class="btn-summary-primary" @click="loadPayrollSummary">
              <i class="fa fa-refresh mr-1"></i> Generate
            </CButton>
          </CCol> -->
        </CRow>
      </CCard>

      <CRow class="mt-1" v-if="hasGenerated">
        <CCol lg="4" md="4" sm="12" v-for="card in summaryCards" :key="card.label">
          <CCard class="summary-card p-3 mb-2" :class="summaryCardClass(card.label)">
            <div class="summary-icon"><i :class="summaryCardIcon(card.label)"></i></div>
            <small class="text-muted d-block card-caption">{{ card.label }}</small>
            <strong class="card-value" :class="{ 'text-danger': card.isDeduction }">{{ formatCurrency(card.value) }}</strong>
          </CCard>
        </CCol>
      </CRow>

      <div class="section-divider my-2" v-if="hasGenerated"></div>

      <CCard class="summary-list-card p-2 p-md-3 mt-2" v-if="hasGenerated">
        <CRow class="align-items-center mb-2">
          <CCol lg="8" md="8" sm="12">
            <strong class="annual-title">Annual Payroll Summary</strong>
            <div class="text-muted record-count">Showing {{ tableRows.length }} records</div>
          </CCol>
          <CCol lg="4" md="4" sm="12" class="text-md-right mt-2 mt-md-0">
            <CButton
              v-if="config.getPermission('payroll_summary').download"
              size="sm"
              color="light"
              class="btn-summary-outline"
              @click="downloadSummary"
            >
              <i class="fa fa-download mr-1"></i> Export CSV
            </CButton>
          </CCol>
        </CRow>

        <div class="mt-1 breadcrumb-wrap" v-if="hasGenerated && breadcrumbItems.length">
          <div class="text-muted breadcrumb-text">
            <span v-for="(item, idx) in breadcrumbItems" :key="item.key">
              <span :class="item.clickable ? 'crumb-link' : ''" @click="item.clickable && item.action()">{{ item.label }}</span>
              <span v-if="idx < breadcrumbItems.length - 1"> &gt; </span>
            </span>
          </div>
        </div>

        <div class="mt-2" v-if="hasGenerated">
          <div class="table-responsive-sm table-responsive-md">
            <CDataTable
              :table-filter="tableFilterOptions"
              pagination
              :items-per-page="10"
              items-per-page-select
              :loading="isLoading"
              :items="tableRows"
              :fields="cdataFields"
              :sorter="cdataSorter"
              hover
              outlined
            >
              <template #totalEarnings="{ item }">
                <td class="py-2">{{ formatCurrency(item.totalEarnings) }}</td>
              </template>
              <template #totalDeductions="{ item }">
                <td class="py-2">
                  <span :class="{ 'text-danger': isHighDeduction(item) }">
                    {{ formatCurrency(item.totalDeductions) }}
                  </span>
                </td>
              </template>
              <template #netPay="{ item }">
                <td class="py-2">
                  <strong :class="{ 'text-danger': isHighDeduction(item) }">
                    {{ formatCurrency(item.netPay) }}
                  </strong>
                </td>
              </template>
              <template #employeeName="{ item }">
                <td class="py-2">{{ item.employeeName }}</td>
              </template>
              <template #payrollNo="{ item }">
                <td class="py-2">{{ item.payrollNo }}</td>
              </template>
              <template #period="{ item }">
                <td class="py-2">{{ item.period }}</td>
              </template>
              <template #earnings="{ item }">
                <td class="py-2">{{ formatCurrency(item.earnings) }}</td>
              </template>
              <template #deductions="{ item }">
                <td class="py-2">
                  <span :class="{ 'text-danger': isHighDeduction(item) }">
                    {{ formatCurrency(item.deductions) }}
                  </span>
                </td>
              </template>
              <template #action="{ item }">
                <td class="py-2" style="width: 180px">
                  <CButton size="sm" color="info" @click="handleView(item)">
                    <i class="fa fa-eye mr-1"></i>View Payroll
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>
        </div>
      </CCard>

      <div v-if="isLoading && !hasGenerated" class="text-muted mt-4">
        Loading payroll summary…
      </div>
      <div v-else-if="!hasGenerated" class="text-muted mt-4">
        Select a year and click Generate to load payroll summary.
      </div>
    </CCard>

    <CModal
      :show.sync="showPayrollDetailModal"
      size="xl"
      addContentClasses="payslip-modal-content"
      class="payslip-detail-modal"
    >
      <template #header>
        <div class="w-100 d-flex justify-content-between align-items-center">
          <span class="payslip-modal-title text-muted mb-0">Payroll Detail</span>
          <button type="button" class="close" aria-label="Close" @click="closePayslipModal">&times;</button>
        </div>
      </template>

      <div v-if="modalPayslipLoading" class="text-center py-5 text-muted">Loading payroll detail…</div>
      <div v-else-if="payslipDetail" class="payslip-detail-body payslip-print-root">
        <div class="payslip-hero d-flex flex-wrap align-items-start justify-content-between">
          <div class="payslip-hero-left mb-3 mb-md-0 pr-md-3">
            <h3 class="payslip-employee-name mb-1">{{ payslipDetail.name || "—" }}</h3>
            <div class="payslip-subline text-muted">{{ payslipPositionArea }}</div>
            <div class="payslip-period text-muted small mt-2">
              Pay Period: {{ formatPayPeriodLabel(payslipDetail) }}
            </div>
           
          </div>
         </div>

        <hr class="payslip-hero-rule" />

        <!-- <div class="payslip-summary-strip text-muted small mb-3">
          <i class="fa fa-money text-success mr-1"></i>
          <span>NET PAY</span>
          <span class="mx-2">·</span>
          <span>Deductions</span>
          <strong class="payslip-summary-ded ml-1">{{ formatCurrency(payslipDetail.total_deductions) }}</strong>
        </div> -->

        <CRow class="payslip-columns">
          <CCol lg="6" md="12" class="mb-3 mb-lg-0">
            <div class="payslip-col-title payslip-col-title-earnings mb-2">
              <i class="fa fa-briefcase mr-2"></i> EARNINGS
            </div>
            <div class="table-responsive">
              <table class="table table-bordered table-sm payslip-table payslip-table-earnings text-left mb-0">
                <thead>
                  <tr>
                    <th>Earnings</th>
                    <th class="text-center">Trips / Hrs</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Basic Salary</td>
                    <td class="text-center">{{ payslipDetail.basic_salary_qty != null && payslipDetail.basic_salary_qty !== "" ? payslipDetail.basic_salary_qty : "—" }}</td>
                    <td class="text-right">{{ formatPayslipCell(payslipDetail.basic_salary_amount) }}</td>
                  </tr>
                  <tr>
                    <td>Holiday Pay</td>
                    <td class="text-center">{{ payslipDetail.holiday_qty != null && payslipDetail.holiday_qty !== "" ? payslipDetail.holiday_qty : "—" }}</td>
                    <td class="text-right">{{ formatPayslipCell(payslipDetail.holiday_qty_amount) }}</td>
                  </tr>
                  <tr>
                    <td>Overtime</td>
                    <td class="text-center">—</td>
                    <td class="text-right">{{ formatPayslipCell(payslipDetail.overtime) }}</td>
                  </tr>
                  <tr>
                    <td>Allowance</td>
                    <td class="text-center">—</td>
                    <td class="text-right">{{ formatPayslipCell(payslipDetail.allowance) }}</td>
                  </tr>
                  <tr>
                    <td>Adjustment</td>
                    <td class="text-center">—</td>
                    <td class="text-right">{{ formatPayslipCell(payslipDetail.adjustment) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="payslip-tfoot-earnings">
                    <th colspan="2">TOTAL</th>
                    <th class="text-right">{{ formatCurrency(payslipDetail.total_earnings) }}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>

          <CCol lg="6" md="12">
            <div class="payslip-col-title payslip-col-title-deductions mb-2">
              <i class="fa fa-minus-circle mr-2"></i> DEDUCTIONS
            </div>
            <div class="table-responsive">
              <table class="table table-bordered table-sm payslip-table payslip-table-deductions text-left mb-0">
                <thead>
                  <tr>
                    <th>Deductions</th>
                    <th class="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ded in payslipDeductionRows" :key="ded.label">
                    <td>{{ ded.label }}</td>
                    <td class="text-right">{{ formatPayslipCell(payslipDeductionAmount(ded.label)) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="payslip-tfoot-deductions">
                    <th>TOTAL</th>
                    <th class="text-right">{{ formatCurrency(payslipDetail.total_deductions) }}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>
        </CRow>

        <div class="payslip-footer-bar mt-4 d-flex align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center text-white mb-2 mb-md-0">
            <i class="fa fa-money payslip-footer-icon mr-3"></i>
            <span class="text-uppercase font-weight-bold payslip-footer-label">Net Pay</span>
          </div>
          <div class="payslip-footer-amount text-white font-weight-bold">{{ formatCurrency(payslipDetail.net_pay) }}</div>
        </div>
      </div>
      <div v-else-if="!modalPayslipLoading" class="text-muted text-center py-3">No payroll detail to display.</div>
      <template #footer>
        <CButton color="secondary" size="sm" @click="closePayslipModal">Close</CButton>
      </template>
    </CModal>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import config from "../../config.js";
import Swal from "sweetalert2";

/** Deduction breakdown keys (map keys) — order matches CSV between TOTAL EARNINGS and Total Deductions. */
const PAYROLL_HISTORY_CSV_DEDUCTION_COLUMNS = [
  "RIDER LATE",
  "CHARGES",
  "MEALS",
  "SSS",
  "SSS LOAN",
  "PHIC",
  "PAG-IBIG",
  "PAG-IBIG STL",
  "UTILITIES",
  "CASH ADVANCE",
  "OTHER DEDUCTIONS",
];

/** Full history CSV header row (column order). */
const PAYROLL_HISTORY_CSV_HEADERS = [
  "PAYROLL NO.",
  "PERSONNEL NAME",
  "PERIOD",
  "TOTAL EARNINGS",
  ...PAYROLL_HISTORY_CSV_DEDUCTION_COLUMNS,
  "TOTAL DEDUCTIONS",
  "NETPAY",
];

/** payroll_detail-style keys from API → CSV column title */
const PAYROLL_DEDUCTION_RAW_TO_COLUMN = {
  rider_late: "RIDER LATE",
  charges: "CHARGES",
  meals: "MEALS",
  sss: "SSS",
  sss_loan: "SSS LOAN",
  phic: "PHIC",
  pag_ibig: "PAG-IBIG",
  pag_ibig_stl: "PAG-IBIG STL",
  utilities: "UTILITIES",
  cash_advance: "CASH ADVANCE",
  other_deductions: "OTHER DEDUCTIONS",
};

export default {
  name: "PayrollSummary",
  async mounted() {
    if (!config.getPermission("payroll_summary").view) {
      return;
    }
    await this.getPersonnelList();
    await this.loadPayrollSummary();
  },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      config,
      selectedYear: currentYear,
      hasGenerated: false,
      isLoading: false,
      currentView: "employee",
      selectedEmployee: null,
      selectedPayrollRow: null,
      showPayrollDetailModal: false,
      payslipDetail: null,
      modalPayslipLoading: false,
      yearSummary: null,
      employeeSummaryRows: [],
      employeePayrollRows: [],
      personnelMap: {},
    };
  },
  computed: {
    hasPayrollSummaryView() {
      return config.getPermission("payroll_summary").view;
    },
    payslipDeductionRows() {
      return [
        { label: "Rider Late" },
        { label: "Charges" },
        { label: "Meals" },
        { label: "SSS" },
        { label: "SSS Loan" },
        { label: "PHIC" },
        { label: "Pag-ibig" },
        { label: "Pag-ibig STL" },
        { label: "Utilities" },
        { label: "Cash Advance" },
        { label: "Other Deductions" },
      ];
    },
    payslipPositionArea() {
      const d = this.payslipDetail;
      if (!d) return "—";
      const pos = (d.position && String(d.position).trim()) || "";
      const area = (d.area && String(d.area).trim()) || "";
      if (pos && area) return `${pos} · ${area}`;
      return pos || area || "—";
    },
   
    availableYears() {
      const thisYear = new Date().getFullYear();
      return [0, 1, 2, 3, 4, 5].map((step) => thisYear - step);
    },
    historyRows() {
      return this.employeePayrollRows;
    },
    cdataSorter() {
      return true;
    },
    tableFilterOptions() {
      if (!this.hasGenerated) return false;
      if (this.currentView === "employee") return { placeholder: "Employee name" };
      return { placeholder: "Payroll No" };
    },
    cdataFields() {
      if (this.currentView === "employee") {
        return [
          { key: "employeeName", label: "Employee Name" },
          { key: "totalEarnings", label: "Total Earnings" },
          { key: "totalDeductions", label: "Total Deductions" },
          { key: "netPay", label: "Net Pay" },
          { key: "action", label: "Action", sorter: false },
        ];
      }
      return [
        { key: "payrollNo", label: "Payroll No" },
        { key: "period", label: "Period (Start - End)" },
        { key: "earnings", label: "Earnings" },
        { key: "deductions", label: "Deductions" },
        { key: "netPay", label: "Net Pay" },
        { key: "action", label: "Action", sorter: false },
      ];
    },
    tableRows() {
      if (this.currentView === "employee") return this.employeeSummaryRows;
      return this.historyRows;
    },
    breadcrumbItems() {
      const crumbs = [{ key: "root", label: "Payroll Summary", clickable: false, action: () => {} }];
      crumbs.push({
        key: "year",
        label: String(this.selectedYear),
        clickable: this.currentView === "history",
        action: () => this.backToEmployee(),
      });
      if (this.currentView === "history" && this.selectedEmployee) {
        crumbs.push({ key: "employee", label: this.selectedEmployee.employeeName, clickable: false, action: () => {} });
      }
      return crumbs;
    },
    summaryCards() {
      if (this.currentView === "employee" && this.yearSummary) {
        return [
          { label: "Total Earnings", value: Number(this.yearSummary.total_earnings || 0) },
          { label: "Total Deductions", value: Number(this.yearSummary.total_deductions || 0), isDeduction: true },
          { label: "Net Pay", value: Number(this.yearSummary.total_net_pay || 0) },
        ];
      }
      const rows = this.tableRows;
      const totals = rows.reduce((acc, row) => {
        acc.earnings += row.totalEarnings != null ? row.totalEarnings : row.earnings || 0;
        acc.deductions += row.totalDeductions != null ? row.totalDeductions : row.deductions || 0;
        acc.net += row.netPay || 0;
        return acc;
      }, { earnings: 0, deductions: 0, net: 0 });
      return [
        { label: "Total Earnings", value: totals.earnings },
        { label: "Total Deductions", value: totals.deductions, isDeduction: true },
        { label: "Net Pay", value: totals.net },
      ];
    },
  },
  methods: {
    summaryCardClass(label) {
      if (label === "Total Earnings") return "card-earnings";
      if (label === "Total Deductions") return "card-deductions";
      return "card-netpay";
    },
    summaryCardIcon(label) {
      if (label === "Total Earnings") return "fa fa-dollar";
      if (label === "Total Deductions") return "fa fa-times-circle-o";
      return "fa fa-calendar-check-o";
    },
    yearSubtitle(year) {
      const current = Number(this.selectedYear);
      if (!current || !year) return "";
      if (year === current) return "Current year";
      if (year === current - 1) return "Previous year";
      return `${current - year} years ago`;
    },
    getAuthHeaders() {
      return {
        Authorization: "Bearer " + window.localStorage.getItem("jwt"),
      };
    },
    async apiGet(path, params = {}) {
      return axios.get(`${config.api_path}${path}`, {
        params,
        headers: this.getAuthHeaders(),
      });
    },
    extractFirstArray(payload, maxDepth = 6) {
      if (maxDepth < 0 || payload == null) return [];
      if (typeof payload === "string") {
        try {
          const parsed = JSON.parse(payload);
          return this.extractFirstArray(parsed, maxDepth - 1);
        } catch (e) {
          return [];
        }
      }
      if (Array.isArray(payload)) return payload;
      if (typeof payload !== "object") return [];

      const numericObjectRows = Object.keys(payload)
        .filter((key) => /^\d+$/.test(key))
        .map((key) => payload[key])
        .filter((item) => item && typeof item === "object");
      if (numericObjectRows.length) {
        return numericObjectRows;
      }

      const directKeys = ["data", "rows", "result", "items", "list"];
      for (const key of directKeys) {
        if (Array.isArray(payload[key])) return payload[key];
      }

      for (const key of Object.keys(payload)) {
        const value = payload[key];
        if (Array.isArray(value)) return value;
      }

      for (const key of Object.keys(payload)) {
        const nested = this.extractFirstArray(payload[key], maxDepth - 1);
        if (nested.length) return nested;
      }

      return [];
    },
    normalizeListResponse(payload) {
      return this.extractFirstArray(payload);
    },
    resolveEmployeeName(personnelId) {
      return this.personnelMap[String(personnelId)] || `Personnel #${personnelId}`;
    },
    async getPersonnelList() {
      try {
        const response = await this.apiGet(`/personnel`, { page: 1, limit: 1000000 });
        const personnelData = this.normalizeListResponse(response.data);
        this.personnelMap = personnelData.reduce((acc, item) => {
          const id = String(item.id || item.personnel_id || item.personnelId || "");
          const fullName =
            item.full_name ||
            item.fullname ||
            item.employee_name ||
            `${item.lastname || item.last_name || ""}, ${item.firstname || item.first_name || ""}`
              .replace(/^,\s*/, "")
              .trim();
          if (!id) return acc;
          acc[id] = fullName || item.fullname || id;
          return acc;
        }, {});
      } catch (e) {
        this.personnelMap = {};
      }
    },
    async fetchYearlySummary(year) {
      const response = await this.apiGet(`/payroll-summary/yearly`, { year });
      const payload = response.data || {};
      const summaryObject =
        (payload &&
          typeof payload === "object" &&
          (payload.total_earnings != null || payload.totalEarnings != null))
          ? payload
          : payload.data || payload.result || {};
      this.yearSummary = {
        total_earnings: Number(
          summaryObject.total_earnings ?? summaryObject.totalEarnings ?? 0
        ),
        total_deductions: Number(
          summaryObject.total_deductions ?? summaryObject.totalDeductions ?? 0
        ),
        total_net_pay: Number(
          summaryObject.total_net_pay ??
            summaryObject.totalNetPay ??
            summaryObject.net_pay ??
            summaryObject.netPay ??
            0
        ),
      };
    },
    async fetchEmployees(year) {
      const response = await this.apiGet(`/payroll-summary/employees`, { year });
      const rows = this.normalizeListResponse(response.data);
      this.employeeSummaryRows = rows.map((row) => ({
        __rowKey: "emp-" + (row.personnel_id || row.personnelId || row.id || Math.random()),
        personnelId: String(row.personnel_id || row.personnelId || row.id || ""),
        employeeName:
          row.employee_name ||
          row.employeeName ||
          row.full_name ||
          row.fullname ||
          this.resolveEmployeeName(row.personnel_id || row.personnelId || row.id || ""),
        totalEarnings: Number(row.total_earnings ?? row.totalEarnings ?? row.earnings ?? 0),
        totalDeductions: Number(row.total_deductions ?? row.totalDeductions ?? row.deductions ?? 0),
        netPay: Number(row.total_net_pay ?? row.totalNetPay ?? row.net_pay ?? row.netPay ?? 0),
        raw: row,
      }));
    },
    async fetchEmployeePayrolls(year, personnelId) {
      const response = await this.apiGet(`/payroll-summary/employee-payrolls`, {
        year,
        personnel_id: personnelId,
      });
      const rows = this.normalizeListResponse(response.data);
      this.employeePayrollRows = rows.map((row) => ({
        __rowKey: "payroll-" + row.payroll_no,
        payrollNo: row.payroll_no,
        period: `${this.formatDateOnly(row.payroll_date_from)} - ${this.formatDateOnly(row.payroll_date_to)}`,
        earnings: Number(row.total_earnings || 0),
        deductions: Number(row.total_deductions || 0),
        netPay: Number(row.net_pay || 0),
        personnelId: String(personnelId),
        raw: row,
      }));
    },
    async fetchPersonnelPayrollDetail(personnelId, payrollNo) {
      const response = await this.apiGet(`/payroll-summary/personnel-payroll-detail`, {
        personnel_id: personnelId,
        payroll_no: payrollNo,
        year: Number(this.selectedYear) || new Date().getFullYear(),
      });
      return this.extractPayrollPersonnelPayload(response) || {};
    },
    matchDeductionTypeToExportColumn(typeLabel) {
      const s = String(typeLabel || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
      if (!s) return null;
      if ((s.includes("rider") && s.includes("late")) || s === "rider_late") return "RIDER LATE";
      if (s === "charges") return "CHARGES";
      if (s === "meals") return "MEALS";
      if (s.includes("loan") && s.includes("sss")) return "SSS LOAN";
      if (s === "sss" || s === "sss contribution" || s === "sss ee" || s === "sss er") return "SSS";
      if (s === "phic" || s.includes("philhealth")) return "PHIC";
      if (s.includes("pag") && s.includes("ibig") && (s.includes("stl") || s.includes("short"))) return "PAG-IBIG STL";
      if (s.includes("pag") && s.includes("ibig")) return "PAG-IBIG";
      if (s === "utilities") return "UTILITIES";
      if (s.includes("cash") && s.includes("advance")) return "CASH ADVANCE";
      if (s.includes("other")) return "OTHER DEDUCTIONS";
      return null;
    },
    buildHistoryDeductionColumnMap(raw, deductionList) {
      const map = Object.fromEntries(PAYROLL_HISTORY_CSV_DEDUCTION_COLUMNS.map((h) => [h, 0]));
      (deductionList || []).forEach((item) => {
        const label = item.type || item.label || item.name;
        const col = this.matchDeductionTypeToExportColumn(label);
        if (col) {
          map[col] += Number(item.amount ?? item.value ?? 0) || 0;
        }
      });
      const r = raw && typeof raw === "object" ? raw : {};
      Object.keys(PAYROLL_DEDUCTION_RAW_TO_COLUMN).forEach((key) => {
        if (r[key] == null || r[key] === "") return;
        const col = PAYROLL_DEDUCTION_RAW_TO_COLUMN[key];
        if (col && Object.prototype.hasOwnProperty.call(map, col)) {
          map[col] = Number(r[key]) || 0;
        }
      });
      return map;
    },
    sanitizeFilePart(name) {
      return String(name || "employee")
        .replace(/[/\\?%*:|"<>]/g, "")
        .replace(/\s+/g, "_")
        .slice(0, 80);
    },
    showLoadError(message = "Failed to load payroll summary data.", error = null) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "";
      const statusText = error?.response?.status ? `HTTP ${error.response.status}` : "";
      const requestUrl = error?.config?.url || "";
      const diagnostic = [statusText, requestUrl].filter(Boolean).join(" - ");
      Swal.fire({
        title: "Error",
        text: backendMessage
          ? `${message} (${backendMessage})`
          : diagnostic
          ? `${message} (${diagnostic})`
          : message,
        icon: "error",
      });
    },
    async loadPayrollSummary() {
      if (!this.selectedYear) {
        this.selectedYear = new Date().getFullYear();
      }
      this.isLoading = true;
      this.currentView = "employee";
      this.selectedEmployee = null;
      this.selectedPayrollRow = null;
      this.employeePayrollRows = [];
      try {
        await this.fetchYearlySummary(this.selectedYear);
      } catch (e) {
        this.showLoadError("Failed to load payroll summary yearly data.", e);
        this.isLoading = false;
        return;
      }

      try {
        await this.fetchEmployees(this.selectedYear);
      } catch (e) {
        this.showLoadError("Failed to load payroll summary employee data.", e);
        this.isLoading = false;
        return;
      } finally {
        this.isLoading = false;
      }
      this.hasGenerated = true;
    },
    formatCurrency(amount) {
      const value = Number(amount || 0);
      return "₱" + value.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    firstDefined(...vals) {
      for (let i = 0; i < vals.length; i += 1) {
        const v = vals[i];
        if (v != null && v !== "") return v;
      }
      return null;
    },
    formatPayslipCell(val) {
      const v = this.firstDefined(val);
      if (v == null) return "—";
      const n = Number(v);
      if (!Number.isFinite(n) || n === 0) return "—";
      return n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatPayPeriodLabel(detail) {
      if (!detail) return "";
      const from = detail.payroll_date_from;
      const to = detail.payroll_date_to;
      if (from && moment(from).isValid()) {
        const a = moment(from).format("MMMM DD, YYYY");
        const b = to && moment(to).isValid() ? moment(to).format("MMMM DD, YYYY") : "";
        return b ? `${a} - ${b}` : a;
      }
      return this.selectedPayrollRow?.period || "";
    },
    payslipDeductionAmount(label) {
      const d = this.payslipDetail;
      if (!d) return null;
      switch (label) {
        case "Rider Late":
          return this.firstDefined(d.rider_late);
        case "Charges":
          return this.firstDefined(d.charges);
        case "Meals":
          return this.firstDefined(d.meals);
        case "SSS":
          return this.firstDefined(d.sss, d.SSS);
        case "SSS Loan":
          return this.firstDefined(d.sss_loan, d.SSS_LOAN);
        case "PHIC":
          return this.firstDefined(d.phic);
        case "Pag-ibig":
          return this.firstDefined(d.pag_ibig);
        case "Pag-ibig STL":
          return this.firstDefined(d.pag_ibig_stl);
        case "Utilities":
          return this.firstDefined(d.utilities);
        case "Cash Advance":
          return this.firstDefined(d.cash_advance);
        case "Other Deductions":
          return this.firstDefined(d.other_deductions);
        default:
          return null;
      }
    },
    closePayslipModal() {
      this.showPayrollDetailModal = false;
      this.payslipDetail = null;
      this.modalPayslipLoading = false;
    },
    downloadPayslip() {
      this.$nextTick(() => {
        window.print();
      });
    },
    extractPayrollPersonnelPayload(res) {
      const body = res && res.data != null ? res.data : {};
      const nested =
        body.data !== undefined && body.data !== null && typeof body.data === "object"
          ? body.data
          : body.result !== undefined && body.result !== null && typeof body.result === "object"
            ? body.result
            : null;
      if (nested && !Array.isArray(nested)) {
        return nested;
      }
      if (body && typeof body === "object" && !Array.isArray(body)) {
        const looksLikeDetail =
          body.name != null ||
          body.total_earnings != null ||
          body.net_pay != null ||
          body.basic_salary_amount != null ||
          body.payroll_date_from != null;
        if (looksLikeDetail) return body;
      }
      const fromList = this.extractFirstArray(body);
      if (fromList.length === 1 && typeof fromList[0] === "object" && fromList[0] !== null) {
        return fromList[0];
      }
      return null;
    },
    async loadPayslipDetail(row) {
      const pid = String(row.personnelId);
      const year = Number(this.selectedYear) || new Date().getFullYear();
      const res = await this.apiGet(`/payroll-summary/personnel-payroll-detail`, {
        personnel_id: pid,
        payroll_no: row.payrollNo,
        year,
      });
      const data = this.extractPayrollPersonnelPayload(res);
      if (data && typeof data === "object") {
        this.payslipDetail = data;
        return;
      }
      this.payslipDetail = null;
      const err = new Error("Payroll detail response was empty or could not be parsed.");
      err.response = res;
      throw err;
    },
    formatDateOnly(value) {
      if (!value) return "";
      const raw = String(value).trim();
      if (!raw) return "";
      if (raw.includes("T")) return raw.split("T")[0];
      if (raw.includes(" ")) return raw.split(" ")[0];
      return raw;
    },
    toCsvCell(value) {
      const text = value == null ? "" : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    },
    isHighDeduction(row) {
      const earnings = row.totalEarnings != null ? row.totalEarnings : row.earnings || 0;
      const deductions = row.totalDeductions != null ? row.totalDeductions : row.deductions || 0;
      if (!earnings) return false;
      return deductions / earnings >= 0.3;
    },
    async handleView(row) {
      if (this.currentView === "employee") {
        this.selectedEmployee = {
          employeeName: row.employeeName,
          personnelId: row.personnelId,
        };
        this.isLoading = true;
        try {
          await this.fetchEmployeePayrolls(this.selectedYear, row.personnelId);
          this.currentView = "history";
        } catch (e) {
          this.showLoadError("Failed to load employee payroll history.", e);
        } finally {
          this.isLoading = false;
        }
        return;
      }
      this.selectedPayrollRow = row;
      this.payslipDetail = null;
      this.modalPayslipLoading = true;
      try {
        await this.loadPayslipDetail(row);
        this.showPayrollDetailModal = true;
      } catch (e) {
        this.showLoadError("Failed to load payroll detail.", e);
      } finally {
        this.modalPayslipLoading = false;
      }
    },
    backToEmployee() {
      this.currentView = "employee";
      this.selectedPayrollRow = null;
    },
    async downloadSummary() {
      if (!this.hasGenerated) return;
      const showLoading = typeof this.$showLoading === "function";
      const lines = [];
      let filename = `payroll-summary-${this.selectedYear}.csv`;

      if (this.currentView === "employee") {
        lines.push("Employee Name,Total Earnings,Total Deductions,Net Pay");
        this.tableRows.forEach((row) => {
          lines.push([
            this.toCsvCell(row.employeeName),
            this.toCsvCell(row.totalEarnings),
            this.toCsvCell(row.totalDeductions),
            this.toCsvCell(row.netPay),
          ].join(","));
        });
      } else {
        const personnelName =
          this.selectedEmployee?.employeeName || this.resolveEmployeeName(this.tableRows[0]?.personnelId);
        filename = `payroll-summary-${this.selectedYear}-${this.sanitizeFilePart(personnelName)}.csv`;

        lines.push(PAYROLL_HISTORY_CSV_HEADERS.map((h) => this.toCsvCell(h)).join(","));

        if (showLoading) this.$showLoading(true, "Preparing export with deduction breakdown…");
        try {
          const rowsWithDeductions = await Promise.all(
            this.tableRows.map(async (row) => {
              let payrollDetail = {};
              try {
                payrollDetail = await this.fetchPersonnelPayrollDetail(row.personnelId, row.payrollNo);
              } catch (e) {
                payrollDetail = {};
              }
              return {
                row,
                dedMap: this.buildHistoryDeductionColumnMap(payrollDetail || row.raw || {}, []),
              };
            })
          );
          rowsWithDeductions.forEach(({ row, dedMap }) => {
            const dedCells = PAYROLL_HISTORY_CSV_DEDUCTION_COLUMNS.map((col) => this.toCsvCell(dedMap[col] ?? 0));
            lines.push(
              [
                this.toCsvCell(row.payrollNo),
                this.toCsvCell(personnelName),
                this.toCsvCell(row.period),
                this.toCsvCell(row.earnings),
                ...dedCells,
                this.toCsvCell(row.deductions),
                this.toCsvCell(row.netPay),
              ].join(",")
            );
          });
        } catch (e) {
          this.showLoadError("Failed to build payroll export.", e);
          return;
        } finally {
          if (showLoading) this.$showLoading(false);
        }
      }

      const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.payroll-summary {
  background: #f7f8fc;
  border: none;
  box-shadow: none;
}
.summary-title {
  font-size: 26px;
  font-weight: 700;
  color: #1f2937;
}
.summary-subtitle {
  color: #6b7280;
}
.btn-summary-primary {
  background: #2f56d8;
  border-color: #2f56d8;
}
.btn-summary-outline {
  border: 1px solid #d8dce7;
  background: #fff;
}
.year-filter-card {
  border: 1px solid #e3e7f0;
  border-radius: 10px;
}
.field-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #8c95a7;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.payroll-summary .summary-card {
  border: 1px solid #e5e7eb;
  border-top-width: 3px;
  border-radius: 10px;
}
.summary-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-bottom: 10px;
}
.card-caption {
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.4px;
}
.card-value {
  font-size: 36px;
  font-weight: 700;
}
.card-earnings {
  border-top-color: #4b78ff;
}
.card-earnings .summary-icon {
  background: #edf2ff;
  color: #4b78ff;
}
.card-earnings .card-value {
  color: #365cc9;
}
.card-deductions {
  border-top-color: #ef4444;
}
.card-deductions .summary-icon {
  background: #ffeef0;
  color: #dc2626;
}
.card-deductions .card-value {
  color: #b91c1c;
}
.card-netpay {
  border-top-color: #10b981;
}
.card-netpay .summary-icon {
  background: #ecfdf5;
  color: #047857;
}
.card-netpay .card-value {
  color: #1f7a4c;
}
.section-divider {
  border-top: 3px solid #eceff5;
}
.summary-list-card {
  border: 1px solid #e4e8f2;
  border-radius: 10px;
}
.annual-title {
  font-size: 14px;
}
.record-count {
  font-size: 12px;
}
.breadcrumb-wrap {
  margin-bottom: 10px;
}
.breadcrumb-text {
  font-size: 19px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: 0.01em;
}
.crumb-link {
  color: #1352a1;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
}
.payslip-detail-modal .payslip-modal-title {
  font-size: 0.95rem;
  font-weight: 600;
}
.payslip-detail-modal .payslip-modal-content {
  border-radius: 12px;
  overflow: hidden;
}
.payslip-detail-modal .modal-body {
  background: #f3f4f6;
  padding: 1rem 1.25rem 1.5rem;
}
.payslip-detail-body {
  background: #fff;
  border-radius: 10px;
  padding: 1.25rem 1.25rem 0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}
.payslip-employee-name {
  color: #1e3a5f;
  font-weight: 700;
  font-size: 1.35rem;
  letter-spacing: 0.02em;
}
.payslip-subline {
  font-size: 0.95rem;
}
.payslip-hero-rule {
  border-top: 1px solid #e5e7eb;
  margin: 1rem 0;
}
.payslip-summary-ded {
  color: #b91c1c;
}
.payslip-net-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #059669, #047857);
  color: #fff;
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.9rem;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.35);
}
.payslip-net-badge .payslip-net-badge-label {
  font-weight: 600;
  margin-right: 0.5rem;
  letter-spacing: 0.04em;
}
.payslip-net-badge strong {
  font-size: 1.05rem;
}
.payslip-col-title {
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.06em;
}
.payslip-col-title-earnings {
  color: #047857;
}
.payslip-col-title-deductions {
  color: #be123c;
}
.payslip-table {
  font-size: 0.875rem;
}
.payslip-table-earnings thead th {
  background: #d1fae5;
  color: #065f46;
  font-weight: 700;
  font-size: 0.78rem;
  border-color: #a7f3d0 !important;
}
.payslip-table-earnings tbody td {
  border-color: #e5e7eb !important;
  vertical-align: middle;
}
.payslip-tfoot-earnings th {
  background: #a7f3d0;
  color: #065f46;
  font-weight: 700;
  border-color: #6ee7b7 !important;
}
.payslip-table-deductions thead th {
  background: #fce7f3;
  color: #9f1239;
  font-weight: 700;
  font-size: 0.78rem;
  border-color: #fbcfe8 !important;
}
.payslip-table-deductions tbody td {
  border-color: #e5e7eb !important;
  vertical-align: middle;
}
.payslip-tfoot-deductions th {
  background: #fbcfe8;
  color: #b91c1c;
  font-weight: 700;
  border-color: #f9a8d4 !important;
}
.payslip-footer-bar {
  background: linear-gradient(90deg, #047857, #059669);
  border-radius: 0 0 10px 10px;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  padding: 1rem 1.5rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
}
.payslip-footer-icon {
  font-size: 1.75rem;
  opacity: 0.95;
}
.payslip-footer-label {
  font-size: 1rem;
  letter-spacing: 0.12em;
}
.payslip-footer-amount {
  font-size: 1.5rem;
  letter-spacing: 0.02em;
}
</style>

<style>
/* Print payslip (Download Payslip → save as PDF in the print dialog) */
@media print {
  body * {
    visibility: hidden !important;
  }
  .modal.show,
  .modal.show * {
    visibility: visible !important;
  }
  .modal.show {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
  .modal.show .modal-dialog {
    max-width: 100% !important;
    margin: 0 !important;
  }
  .modal.show .modal-content {
    border: none !important;
    box-shadow: none !important;
  }
  .modal.show .modal-header,
  .modal.show .modal-footer {
    display: none !important;
  }
  .modal.show .modal-body {
    background: #fff !important;
    padding: 0.5rem 0 !important;
  }
  .payslip-print-root {
    box-shadow: none !important;
    border-radius: 0 !important;
  }
  .payslip-btn-download {
    display: none !important;
  }
  .modal-backdrop {
    display: none !important;
  }
}
</style>
